import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  Button,
  Input,
  Spinner,
  Pressable,
  useToast,
} from "native-base";
import { StyleSheet, Platform, View } from "react-native";
import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";
import { GlobalConstants } from "../../constants/constants";


// const buildApiUrl = (baseUrl, port) => {
//   const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
//   return `${cleanBaseUrl}:${port}`;
// };

// const LOYALTY_API_BASE_URL = buildApiUrl(
//   GlobalConstants.servicesBaseUrl,
//   "3005"
// );
const LOYALTY_API_BASE_URL = `${GlobalConstants.LoyaltyBaseUrl}`.replace(/\/$/, "");

const makeApiCall = async (
  method,
  endpoint,
  data = null,
  token,
  retries = 2
) => {
  const url = `${LOYALTY_API_BASE_URL}${endpoint}`;
  const config = {
    method,
    url,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    timeout: 30000,
  };
  if (data && ["POST", "PUT", "PATCH"].includes(method)) config.data = data;
  else if (data && method === "GET") config.params = data;
  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (
      retries > 0 &&
      (error.code === "ECONNABORTED" || error.message.includes("timeout"))
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return makeApiCall(method, endpoint, data, token, retries - 1);
    }
    throw error;
  }
};


const safeNum = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
};

const unwrap = (res) =>
  res && typeof res === "object" && "status" in res
    ? res
    : { status: true, data: res };

const formatPoints = (val, fallback = "0.00") => {
  const n = safeNum(String(val ?? "").trim(), NaN);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
};

const pointsApi = {
  getMyPointsSummary: (token) =>
    makeApiCall("GET", "/api/configuration/user-loyalty-points", null, token),
  redeem: (token, pointsToRedeem, qrCodeData = null) => {
    const body = { points_to_redeem: safeNum(pointsToRedeem, NaN) };
    if (qrCodeData) body.qr_code_data = String(qrCodeData);
    return makeApiCall("POST", "/api/points/redeem", body, token);
  },
  gift: (token, pointsToGift, recipientIdentifier) =>
    makeApiCall(
      "POST",
      "/api/points/gift",
      {
        points_to_gift: safeNum(pointsToGift, NaN),
        recipient_user_id: recipientIdentifier,
      },
      token
    ),
  getTierInfo: (token) =>
    makeApiCall("GET", "/api/configuration/tier-info", null, token),
};


const pageBgStyle = { backgroundColor: GlobalStyles.colors.lightGrey };

const cardShadow = {
  shadowColor: "#000000",
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.25,
  shadowRadius: 4,
  elevation: 4,
  boxShadow: "0px 4px 4px 0px #00000040",
};

const gradientBg = {
  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
  backgroundColor: GlobalStyles.colors.blue,
};

const FONT = GlobalStyles.text.fontFamily;

const RedeemRow = ({ label, value, isLoading }) => (
  <View
    style={{
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <Text
      style={{
        fontFamily: FONT,
        fontSize: 14,
        fontWeight: "400",
        color: "#000000",
        flex: 1,
        marginRight: 12,
      }}
    >
      {label}
    </Text>

    <View
      style={{
        width: 180,
        height: 34,
        borderWidth: 1,
        borderColor: "#000000",
        backgroundColor: "#FFFFFF",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isLoading ? (
        <Spinner size="sm" color={GlobalStyles.colors.blue} />
      ) : (
        <Text
          style={{
            fontFamily: FONT,
            fontSize: 14,
            color: "#000000",
            textAlign: "center",
          }}
        >
          {value}
        </Text>
      )}
    </View>
  </View>
);


const RedeemPointsCitizen = ({ toast, user }) => {
  const [isLoading, setIsLoading]         = useState(true);
  const [isRedeeming, setIsRedeeming]     = useState(false);
  const [currentPoints, setCurrentPoints] = useState("xx");
  const [currentTier, setCurrentTier]     = useState("xx");
  const [tillNextTier, setTillNextTier]   = useState("xx");
  const [redeemHistory, setRedeemHistory] = useState("xx");
  const [redeemAmount, setRedeemAmount]   = useState("");

  const loadData = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      const [loyaltyRaw, tierRaw] = await Promise.all([
        pointsApi.getMyPointsSummary(user.token).catch(() => null),
        pointsApi.getTierInfo(user.token).catch(() => null),
      ]);

      if (loyaltyRaw) {
        const res = unwrap(loyaltyRaw);
        if (res.status && res.data) {
          const lp = res.data.loyaltyPoints || res.data;
          setCurrentPoints(formatPoints(lp.points_available ?? lp.points));
          setRedeemHistory(formatPoints(lp.points_redeemed ?? 0));
        }
      }

      if (tierRaw) {
        const res = unwrap(tierRaw);
        if (res.status && res.data) {
          const d = res.data;
          const tierName =
            d.current_tier_name ||
            d.tier_name ||
            d.current_tier ||
            d.tier_level ||
            "—";
          setCurrentTier(String(tierName));
          const till =
            d.points_till_next_tier ??
            d.points_to_next_tier ??
            d.next_tier_points_needed ??
            null;
          setTillNextTier(till !== null ? formatPoints(till) : "—");
        }
      }
    } catch (err) {
      console.error("RedeemPointsCitizen loadData:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRedeem = async () => {
    const pts = safeNum(String(redeemAmount).replace(/[^0-9.]/g, ""), NaN);
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid points amount to redeem"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    const available = safeNum(currentPoints, 0);
    if (pts > available) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Insufficient points. You have ${currentPoints} points available.`}
          />
        ),
        placement: "top",
        duration: 4000,
      });
      return;
    }
    setIsRedeeming(true);
    try {
      const res = unwrap(await pointsApi.redeem(user.token, pts));
      if (!res.status) throw new Error(res.status_msg || "Redeem failed");
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={`${formatPoints(pts)} points redeemed successfully!`}
          />
        ),
        placement: "top",
        duration: 3000,
      });
      setRedeemAmount("");
      await loadData();
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to redeem points.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <Center flex={1} style={pageBgStyle} px={4}>
      <Box w="100%" maxW="600px" alignSelf="center" pt="20px" pb="24px">

        <Text
          style={{
            fontFamily: FONT,
            fontSize: 18,
            fontWeight: "700",
            color: "#000000",
            marginBottom: 14,
          }}
        >
          Redeem Points – Citizen User
        </Text>

        <View
          style={[
            {
              backgroundColor: "#FFFFFF",
              borderRadius: 12,
              paddingHorizontal: 24,
              paddingTop: 20,
              paddingBottom: 20,
              width: "100%",
            },
            cardShadow,
          ]}
        >
          <VStack space={3} w="100%">
            <RedeemRow
              label="Your Current points:"
              value={currentPoints}
              isLoading={isLoading}
            />
            <RedeemRow
              label="Your current Tier:"
              value={currentTier}
              isLoading={isLoading}
            />
            <RedeemRow
              label="Points Till Next Tier:"
              value={tillNextTier}
              isLoading={isLoading}
            />
            <RedeemRow
              label="Redeem History:"
              value={redeemHistory}
              isLoading={isLoading}
            />

            <View
              style={{
                width: "100%",
                height: 1,
                backgroundColor: "#EEEEEE",
                marginVertical: 2,
              }}
            />

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: "400",
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                Enter Points to Redeem:
              </Text>
              <View
                style={{
                  width: 180,
                  height: 34,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  backgroundColor: "#FFFFFF",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Input
                  value={redeemAmount}
                  onChangeText={setRedeemAmount}
                  variant="unstyled"
                  fontSize={14}
                  color="#000000"
                  keyboardType="numeric"
                  placeholder="0"
                  textAlign="center"
                  style={{
                    fontFamily: FONT,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                  p={0}
                />
              </View>
            </View>

            <Button
              mt={2}
              alignSelf="center"
              w="160px"
              h="38px"
              borderRadius={6}
              bg={GlobalStyles.colors.blue}
              onPress={handleRedeem}
              isLoading={isRedeeming}
              isLoadingText="Redeeming..."
              isDisabled={!redeemAmount || isLoading}
              style={gradientBg}
              _text={{
                color: "#FFFFFF",
                fontFamily: FONT,
                fontWeight: "bold",
                fontSize: 14,
              }}
            >
              Redeem Points
            </Button>
          </VStack>
        </View>
      </Box>
    </Center>
  );
};

const GiftingPointsCitizen = ({ toast, user }) => {
  const [isLoading, setIsLoading]           = useState(true);
  const [isGifting, setIsGifting]           = useState(false);
  const [currentPoints, setCurrentPoints]   = useState("0.00");
  const [recipientInput, setRecipientInput] = useState("");
  const [giftAmount, setGiftAmount]         = useState("");
  const [confirmed, setConfirmed]           = useState(false);
  const [successMsg, setSuccessMsg]         = useState("");

  const loadPoints = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      const res = unwrap(await pointsApi.getMyPointsSummary(user.token));
      if (res.status && res.data) {
        const lp = res.data.loyaltyPoints || res.data;
        setCurrentPoints(
          formatPoints(lp.points_available ?? lp.points, "0.00")
        );
      }
    } catch (err) {
      console.error("GiftingPointsCitizen loadPoints:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadPoints();
  }, [loadPoints]);

  const recipientDisplay = recipientInput.trim() || "XYZ";

  const handleFirstConfirm = () => {
    if (!recipientInput.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter your friend's TownTicks username or email"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    setConfirmed(true);
  };

  const handleGift = async () => {
    const pts = safeNum(String(giftAmount).replace(/[^0-9.]/g, ""), NaN);
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid amount to gift"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    const available = safeNum(currentPoints, 0);
    if (pts > available) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Insufficient points. You have ${currentPoints} points available.`}
          />
        ),
        placement: "top",
        duration: 4000,
      });
      return;
    }
    setIsGifting(true);
    setSuccessMsg("");
    try {
      const res = unwrap(
        await pointsApi.gift(user.token, pts, recipientInput.trim())
      );
      if (!res.status) throw new Error(res.status_msg || "Gift failed");
      const msg = `${formatPoints(pts)} TT points have been given to your friend ${recipientDisplay}`;
      setSuccessMsg(msg);
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.green} message={msg} />
        ),
        placement: "top",
        duration: 3000,
      });
      setRecipientInput("");
      setGiftAmount("");
      setConfirmed(false);
      await loadPoints();
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to gift points.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsGifting(false);
    }
  };

  const ConfirmBtn = ({ onPress, disabled, isProcessing }) => (
    <Pressable
      onPress={onPress}
      disabled={disabled || isProcessing}
      style={{ opacity: disabled || isProcessing ? 0.45 : 1 }}
    >
      <View
        style={{
          height: 40,
          minWidth: 100,
          borderRadius: 4,
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 22,
          background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
          backgroundColor: GlobalStyles.colors.blue,
        }}
      >
        <Text
          style={{
            fontFamily: FONT,
            fontWeight: "bold",
            fontSize: 15,
            color: "#FFFFFF",
          }}
        >
          {isProcessing ? "..." : "Confirm"}
        </Text>
      </View>
    </Pressable>
  );

  return (
    <Center flex={1} style={pageBgStyle} px={4}>
      <Box w="100%" maxW="600px" alignSelf="center" pt="28px" pb="36px">

        <Text
          style={{
            fontFamily: FONT,
            fontSize: 22,
            fontWeight: "700",
            color: "#000000",
            marginBottom: 22,
          }}
        >
          Gifting Points – Citizen User
        </Text>

        <View
          style={{
            width: "100%",
            borderWidth: 1,
            borderColor: "#BDBDBD",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              backgroundColor: "#D9D9D9",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 20,
              paddingVertical: 13,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(0,0,0,0.12)",
            }}
          >
            <Text
              style={{
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: "400",
                color: "#000000",
              }}
            >
              Your Current TownTicks Points:
            </Text>

            <View
              style={{
                borderWidth: 2,
                borderColor: GlobalStyles.colors.blue,
                backgroundColor: "#FFFFFF",
                minWidth: 52,
                height: 40,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 12,
              }}
            >
              {isLoading ? (
                <Spinner size="sm" color={GlobalStyles.colors.blue} />
              ) : (
                <Text
                  style={{
                    fontFamily: FONT,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GlobalStyles.colors.blue,
                  }}
                >
                  {currentPoints}
                </Text>
              )}
            </View>
          </View>

          <View
            style={{
              backgroundColor: "rgba(217,217,217,0.25)",
              paddingHorizontal: 20,
              paddingTop: 18,
              paddingBottom: 10,
            }}
          >
            <Text
              style={{
                fontFamily: FONT,
                fontSize: 14,
                color: "#000000",
                marginBottom: 8,
              }}
            >
              Enter Friend TownTicks username or email
            </Text>
            <View
              style={{
                width: "100%",
                height: 44,
                borderWidth: 1,
                borderColor: "#CCCCCC",
                backgroundColor: "#FFFFFF",
                borderRadius: 4,
                justifyContent: "center",
                paddingHorizontal: 10,
                marginBottom: 18,
              }}
            >
              <Input
                value={recipientInput}
                onChangeText={(v) => {
                  setRecipientInput(v);
                  if (confirmed) setConfirmed(false);
                  if (successMsg) setSuccessMsg("");
                }}
                variant="unstyled"
                fontSize={14}
                color="#000000"
                keyboardType="email-address"
                autoCapitalize="none"
                placeholder=""
                style={{ fontFamily: FONT, width: "100%" }}
                p={0}
              />
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                Gift TT points to your friend {recipientDisplay}?
              </Text>
              <ConfirmBtn
                onPress={handleFirstConfirm}
                disabled={confirmed || !recipientInput.trim()}
              />
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                How much TT points to give?
              </Text>

              <View
                style={{
                  minWidth: 72,
                  height: 40,
                  borderRadius: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 10,
                  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
                  backgroundColor: GlobalStyles.colors.blue,
                }}
              >
                <Input
                  value={giftAmount}
                  onChangeText={setGiftAmount}
                  variant="unstyled"
                  textAlign="center"
                  fontSize={16}
                  fontWeight="bold"
                  color="#FFFFFF"
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor="rgba(255,255,255,0.55)"
                  style={{
                    fontFamily: FONT,
                    width: 56,
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  _focus={{ bg: "transparent" }}
                  p={0}
                  m={0}
                />
              </View>
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                You selected "{giftAmount || "R"}" TT points for gift to {recipientDisplay}?
              </Text>
              <ConfirmBtn
                onPress={handleGift}
                disabled={!giftAmount || !confirmed || isLoading}
                isProcessing={isGifting}
              />
            </View>
          </View>
        </View>

        <View
          style={{
            alignSelf: "center",
            marginTop: 18,
            backgroundColor: "#FFFFFF",
            borderWidth: 1,
            borderColor: "#CCCCCC",
            borderRadius: 4,
            paddingHorizontal: 28,
            paddingVertical: 13,
            boxShadow: "0px 4px 4px 0px #00000040",
            shadowColor: "#000000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 4,
          }}
        >
          <Text
            style={{
              fontFamily: FONT,
              fontSize: 14,
              color: successMsg ? "#000000" : "#9E9E9E",
              textAlign: "center",
            }}
          >
            {successMsg
              ? successMsg
              : `X TT points have been given to your friend ${recipientDisplay}?`}
          </Text>
        </View>
      </Box>
    </Center>
  );
};

const CitizenLoyaltyManager = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState("redeem");
  const toast = useToast();
  const user  = useSelector((state) => state.user.user);

  const getTabColor = (tab) =>
    activeTab === tab
      ? GlobalStyles.colors.mustard
      : GlobalStyles.colors.blue;

  useEffect(() => {
    if (user?.token)
      axios.defaults.headers.common.Authorization = `Bearer ${user.token}`;
  }, [user]);

  return (
    <Box
      w="100%"
      bg={GlobalStyles.colors.lightGrey}
      minH={Platform.OS === "web" ? "100vh" : "100%"}
    >
      <Center>
        <Box w={["92%", 600, 860]} maxW="960px">
          {onBack && (
            <HStack w="100%" mb={3} px={4} mt={4}>
              <Pressable onPress={onBack}>
                <Text
                  color={GlobalStyles.colors.blue}
                  fontWeight="500"
                  fontSize={14}
                  fontFamily={FONT}
                >
                  ← Back to Services
                </Text>
              </Pressable>
            </HStack>
          )}

          <HStack space={2} mb={6} mt={5} justifyContent="center" flexWrap="wrap">
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={getTabColor("redeem")}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => setActiveTab("redeem")}
            >
              <Text
                fontWeight="600"
                fontFamily={FONT}
                color={getTabColor("redeem")}
              >
                Redeem Points
              </Text>
            </Button>

            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={getTabColor("gift")}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => setActiveTab("gift")}
            >
              <Text
                fontWeight="600"
                fontFamily={FONT}
                color={getTabColor("gift")}
              >
                Gifting Points
              </Text>
            </Button>
          </HStack>
        </Box>
      </Center>

      <Box pb="48px">
        {activeTab === "redeem" && (
          <RedeemPointsCitizen toast={toast} user={user} />
        )}
        {activeTab === "gift" && (
          <GiftingPointsCitizen toast={toast} user={user} />
        )}
      </Box>
    </Box>
  );
};

export default CitizenLoyaltyManager;